import React, {
  useState,
  useContext,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import {
  Button,
  Group,
  NumberInput,
  ActionIcon,
  Modal,
  Grid,
  Accordion,
  Title,
  Blockquote,
  Tooltip,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import PlotlyGraphs from "./PlotlyGraphs";
import { UserContext } from "@/contexts/UserContext/UserContext";
import { IconInfoCircle } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { Validatable } from "@/pages/study/StudyStepper";
import _ from "lodash";
import { useDarkModeContext } from "@/hooks/contextHooks/useDarkModeContext";
import { getAccordionStyles } from "@/helpers/getAccordionStyles";
import { useDarkModeColors } from "@/hooks/helperHooks/useDarkModeColors";
import { useFetchVesselDataByTypeAndSize } from "@/hooks/apiHooks/useFetchVesselDataByTypeAndSize";
import { useUpdateDigitalTwin } from "@/hooks/apiHooks/useUpdateDigitalTwin";
import { usePageTransition } from "@/hooks/helperHooks/usePageTransition";
import { TooltipComponent } from "@/components/Tooltip/Tooltip";
import { ClickPositionType } from "@/types/helperTypes";
import { fieldInfoContent } from "@/data/fieldInfoContent";
import styles from "../../css/ship.module.css";

const fieldGroups = {
  general: [
    "vessel.DWT",
    "vessel.lpp_m",
    "vessel.beam_m",
    "vessel.max_brake_power_kW",
    "vessel.draft_m",
    "vessel.displacement_tons",
    "vessel.design_speed_kt",
  ],
  dimensional: [
    "wools.fujiwara.frontal_area_m2",
    "wools.fujiwara.lateral_area_under_deck_m2",
    "wools.fujiwara.lateral_area_over_deck_m2",
  ],
  propeller: [
    "wools.propulsion.nb_propellers",
    "wools.propulsion.propeller.diameter_m",
    "wools.propulsion.propeller.expanded_area_ratio",
    "wools.propulsion.propeller.design_pitch_ratio",
    "wools.propulsion.propeller.nb_blades",
    "wools.propulsion.rudder.mean_chord_m",
    "wools.propulsion.rudder.span_length_m",
  ],
};

const ShipFormSummary = forwardRef<Validatable, {}>((props, ref) => {
  // HOOK for Smooth transitions between pages
  const transition = usePageTransition();

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const context = useContext(UserContext);
  const vesselType = context?.formValues.vesselType;
  const vesselSize = context?.formValues.vesselSize;

  const [clickPosition, setClickPosition] = useState<ClickPositionType>({
    x: 0,
    y: 0,
  });

  const getClickPosition = (event: React.MouseEvent) => {
    const x = event.clientX;
    const y = event.clientY;
    setClickPosition({ x: x, y: y });
  };

  const { data: vesselData, isLoading: isLoadingVesselData } =
    useFetchVesselDataByTypeAndSize(
      vesselType,
      vesselSize ? vesselSize.toString() : undefined,
    );
  const { mutateAsync: updateDigitalTwinMutation } = useUpdateDigitalTwin();

  const { isDarkMode } = useDarkModeContext();
  const accordionStyles = getAccordionStyles(isDarkMode);
  const { getColor } = useDarkModeColors();

  const [formValues, setFormValues] = useState<{ [key: string]: any }>({
    vessel: {},
    wools: {
      fujiwara: {},
      propulsion: {
        propeller: {},
        rudder: {},
      },
      holtrop: null,
    },
  });
  const [recalculatedData, setRecalculatedData] = useState<any>(null);
  const displayData = recalculatedData || vesselData?.result_data;
  const [openedField, setOpenedField] = useState<string | null>(null);
  const [submitting] = useState(false);
  const [isLockConfirmModalOpen, setIsLockConfirmModalOpen] = useState(false); // State to manage modal visibility

  const closeTooltip = () => {
    setOpenedField(null);
  };

  // Define all input fields
  // Define editable fields as an array of field paths
  const editableFields = [
    // Vessel fields
    "vessel.DWT",
    "vessel.lpp_m",
    "vessel.beam_m",
    "vessel.max_brake_power_kW",
    "vessel.draft_m",
    "vessel.displacement_tons",
    "vessel.design_speed_kt",
    // Fujiwara fields
    "wools.fujiwara.frontal_area_m2",
    "wools.fujiwara.lateral_area_under_deck_m2",
    "wools.fujiwara.lateral_area_over_deck_m2",
    // Propulsion fields
    "wools.propulsion.nb_propellers",
    "wools.propulsion.propeller.diameter_m",
    "wools.propulsion.propeller.expanded_area_ratio",
    "wools.propulsion.propeller.design_pitch_ratio",
    "wools.propulsion.propeller.nb_blades",
    "wools.propulsion.rudder.mean_chord_m",
    "wools.propulsion.rudder.span_length_m",
  ];

  const roundToTwoDecimals = (value: number | undefined) => {
    if (typeof value === "number") {
      return Math.round(value * 100) / 100;
    }
    return value;
  };

  useEffect(() => {
    const fetchVesselData = async () => {
      if (vesselType && vesselSize) {
        const data = vesselData;
        setLoading(false);

        const vesselParticulars = data?.result_data?.dtwin?.vessel || {};
        const fujiwaraData = data?.result_data?.dtwin?.wools?.fujiwara || {};
        const propulsionData =
          data?.result_data?.dtwin?.wools?.propulsion || {};

        // Initialize formValues with the required structure
        const initialFormValues: any = {
          vessel: {},
          wools: {
            fujiwara: {},
            propulsion: {},
          },
        };

        editableFields.forEach((fieldPath) => {
          const fieldParts = fieldPath.split(".");
          let source: any;
          let value: any;

          if (fieldParts[0] === "vessel") {
            source = vesselParticulars;
            value = _.get(source, fieldParts.slice(1));
            value = roundToTwoDecimals(value); // Apply rounding
            _.set(initialFormValues, fieldParts, value);
          } else if (fieldParts[0] === "wools") {
            if (fieldParts[1] === "fujiwara") {
              source = fujiwaraData;
              value = _.get(source, fieldParts.slice(2)); // Corrected slice
              value = roundToTwoDecimals(value); // Apply rounding
              _.set(initialFormValues, fieldParts, value);
            } else if (fieldParts[1] === "propulsion") {
              source = propulsionData;
              value = _.get(source, fieldParts.slice(2)); // Corrected slice
              value = roundToTwoDecimals(value); // Apply rounding
              _.set(initialFormValues, fieldParts, value);
            }
          } else {
            value = undefined;
            _.set(initialFormValues, fieldParts, value);
          }
        });

        setFormValues(initialFormValues);
        setRecalculatedData(null);
      } else {
        navigate("/add-ship");
      }
    };
    fetchVesselData();
  }, [vesselData, vesselType, vesselSize, navigate]);

  /*
  const handleInputChange = (field: string, value: number | undefined) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [field]: value,
    }));
  };
 */

  const fieldLabels: { [key: string]: string } = {
    // Vessel fields
    "vessel.name": "Vessel Name",
    "vessel.DWT": "Deadweight Tonnage (DWT)",
    "vessel.max_brake_power_kW": "Max Brake Power (kW)",
    "vessel.load_case_name": "Load Case Name",
    "vessel.lpp_m": "Length Between Perpendiculars (m)",
    "vessel.beam_m": "Beam (m)",
    "vessel.draft_m": "Draft (m)",
    "vessel.displacement_tons": "Displacement (tons)",
    "vessel.design_speed_kt": "Design Speed (kt)",
    // Fujiwara fields
    "wools.fujiwara.length_over_all_m": "Length Overall (m)",
    "wools.fujiwara.beam_m": "Fujiwara Beam (m)",
    "wools.fujiwara.frontal_area_m2": "Frontal Area (m²)",
    "wools.fujiwara.lateral_area_under_deck_m2": "Lateral Area Under Deck (m²)",
    "wools.fujiwara.lateral_area_over_deck_m2": "Lateral Area Over Deck (m²)",
    "wools.fujiwara.distance_lateral_plane_centroid_to_main_section_m":
      "Distance from Lateral Plane Centroid to Main Section (m)",
    "wools.fujiwara.height_lateral_plane_centroid_above_waterline_m":
      "Height of Lateral Plane Centroid Above Waterline (m)",
    "wools.fujiwara.height_top_superstructures_above_waterline_m":
      "Height of Top Superstructures Above Waterline (m)",
    // Propulsion fields
    "wools.propulsion.nb_propellers": "Number of Propellers",
    "wools.propulsion.propeller.diameter_m": "Propeller Diameter (m)",
    "wools.propulsion.propeller.expanded_area_ratio": "Expanded Area Ratio",
    "wools.propulsion.propeller.design_pitch_ratio": "Design Pitch Ratio",
    "wools.propulsion.propeller.nb_blades": "Number of Blades",
    "wools.propulsion.rudder.mean_chord_m": "Rudder Mean Chord (m)",
    "wools.propulsion.rudder.span_length_m": "Rudder Span Length (m)",
  };

  // Handle 'Recalculate' button click

  const handleRecalculate = async () => {
    setLoading(true);
    try {
      // Deep clone the original result_data
      const originalData = JSON.parse(JSON.stringify(vesselData?.result_data));

      if (!originalData) {
        throw new Error("Original vessel data is missing");
      }

      const inputData = JSON.parse(JSON.stringify(originalData.dtwin));

      const mergeEditableFields = (
        source: any,
        target: any,
        path: string[] = [],
      ) => {
        Object.keys(source).forEach((key) => {
          const currentPath = [...path, key];
          const fullPathKey = currentPath.join(".");

          if (
            typeof source[key] === "object" &&
            source[key] !== null &&
            !Array.isArray(source[key])
          ) {
            if (!target[key]) {
              target[key] = {};
            }
            mergeEditableFields(source[key], target[key], currentPath);
          } else {
            if (editableFields.includes(fullPathKey)) {
              target[key] = source[key];
            }
          }
        });
      };

      mergeEditableFields(formValues, inputData);

      if (inputData.vessel?.beam_m) {
        inputData.wools = inputData.wools || {};
        inputData.wools.fujiwara = inputData.wools.fujiwara || {};
        inputData.wools.fujiwara.beam_m = inputData.vessel.beam_m;
      }
      console.log({ ...originalData, dtwin: inputData });

      const recalculated = await updateDigitalTwinMutation({
        ...originalData,
        dtwin: inputData,
      });

      const updatedData = {
        ...originalData,
        dtwin: recalculated.dtwin,
      };
      console.log(updatedData);
      setRecalculatedData(updatedData);
    } catch (error) {
      console.error("Error recalculating data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleNestedInputChange = (
    path: string[],
    value: string | number | undefined,
  ) => {
    setFormValues((prevValues) => {
      const newValues = _.cloneDeep(prevValues);
      _.set(newValues, path, value);
      console.log("newValues", newValues);
      console.log("prevValues", prevValues);
      return newValues;
    });
  };

  const fieldProperties: { [key: string]: any } = {
    "wools.propulsion.rudder.mean_chord_m": {
      precision: 2,
    },
    "wools.propulsion.rudder.span_length_m": {
      precision: 2,
    },
    "wools.propulsion.nb_propellers": {
      min: 1,
      max: 2,
      step: 1,
      precision: 0,
    },
  };

  const renderInputFields = (fields: string[]) => {
    return fields.map((fullPathKey) => {
      const value = _.get(formValues, fullPathKey);
      const isEditable = editableFields.includes(fullPathKey);
      const numericValue = value != null ? Number(value) : undefined;
      if (!isEditable) return null;

      return (
        <Grid.Col span={4} key={fullPathKey}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              marginBottom: 8,
            }}
          >
            <span style={{ flexGrow: 1, color: getColor("title") }}>
              {fieldLabels[fullPathKey] || fullPathKey}
            </span>
            {fieldInfoContent[fullPathKey] && (
              <Tooltip label="Click for more info" withArrow position="right">
                <ActionIcon
                  onClick={() => setOpenedField(fullPathKey)}
                  size="sm"
                  style={{
                    marginLeft: 5,
                    backgroundColor: "transparent",
                  }}
                >
                  <IconInfoCircle size={16} color={getColor("icon")} />
                </ActionIcon>
              </Tooltip>
            )}
          </div>
          <NumberInput
            value={numericValue}
            onChange={(val) =>
              handleNestedInputChange(fullPathKey.split("."), val)
            }
            style={{ marginBottom: "1rem" }}
            {...fieldProperties[fullPathKey]}
          />
        </Grid.Col>
      );
    });
  };

  const handleSubmit = async (locked: boolean) => {
    if (context && vesselType && vesselSize) {
      const {
        shipName = "",
        loadCaseName = "",
        comment = "",
        additionalInfo = "",
        projectId = "",
      } = context.formValues;

      const shipData = {
        ship_name: shipName,
        load_case_name: loadCaseName,
        comment,
        additional_info: additionalInfo,
        vessel_data: recalculatedData || vesselData?.result_data || {},
        vessel_type: vesselType,
        vessel_size: vesselSize.toString(),
        locked: locked,
        projectId: projectId,
      };

      console.log("🛳️ shipData -> " + JSON.stringify(shipData));

      try {
        await context.saveShipData(shipData);
        navigate("/");
      } catch (error) {
        console.error("Error saving ship:", error);
        const err = error as { response?: { data?: any }; message?: string };
        if (err.response && err.response.data) {
          console.error(
            `Error saving ship: ${JSON.stringify(err.response.data, null, 2)}`,
          );
        } else if (err.message) {
          console.error(`Error saving ship: ${err.message}`);
        } else {
          console.error(`Error saving ship: ${JSON.stringify(error, null, 2)}`);
        }
      }
    }
  };

  const validate = () => {
    const requiredFields = [
      // List required editable fields
      "vessel.DWT",
      "vessel.lpp_m",
      "vessel.beam_m",
      "vessel.draft_m",
      "vessel.displacement_tons",
      "vessel.design_speed_kt",
      "wools.fujiwara.frontal_area_m2",
      "wools.fujiwara.lateral_area_under_deck_m2",
      "wools.fujiwara.lateral_area_over_deck_m2",
      // Add other required fields as needed
    ];

    for (const fieldPath of requiredFields) {
      const value = _.get(formValues, fieldPath);
      if (value === undefined || value === null || value === "") {
        notifications.show({
          title: "Validation Error",
          message: `Please fill in the required field: ${
            fieldLabels[fieldPath] || fieldPath
          }`,
          color: "red",
        });
        return false;
      }
    }

    return true;
  };

  useImperativeHandle(ref, () => ({
    validate,
    handleSubmit,
  }));

  /*
  const handleSubmitAndLock = () => {
    setIsLockConfirmModalOpen(true);
  };
   */

  if (isLoadingVesselData || transition) return null;

  return (
    <div onClick={getClickPosition} className={styles.stepContainer}>
      <Accordion multiple variant="contained" styles={accordionStyles}>
        {/* General Particulars Accordion */}
        <Accordion.Item value="general">
          <Accordion.Control>
            <Title size="lg" mt="md" mb="sm">
              General Particulars
            </Title>
          </Accordion.Control>
          <Accordion.Panel>
            <Grid gutter="md">{renderInputFields(fieldGroups.general)}</Grid>
          </Accordion.Panel>
        </Accordion.Item>

        {/* Dimensional Particulars Accordion */}
        <Accordion.Item value="dimensional">
          <Accordion.Control>
            <Title size="lg" mt="md" mb="sm">
              Windage Particulars
            </Title>
          </Accordion.Control>
          <Accordion.Panel>
            <Grid gutter="md">
              {renderInputFields(fieldGroups.dimensional)}
            </Grid>
          </Accordion.Panel>
        </Accordion.Item>

        {/* Propeller Particulars Accordion */}
        <Accordion.Item value="propeller">
          <Accordion.Control>
            <Title size="lg" mt="md" mb="sm">
              Propulsion Particulars
            </Title>
          </Accordion.Control>
          <Accordion.Panel>
            <Grid gutter="md">{renderInputFields(fieldGroups.propeller)}</Grid>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      {/* Other content like buttons */}
      <Group
        mt="lg"
        mb="lg"
        justify="center"
        style={{ width: "100%", margin: "auto" }}
      >
        <Button
          fullWidth
          onClick={handleRecalculate}
          disabled={loading || submitting}
          className={styles.recalculateBtn}
        >
          {loading ? "Recalculating..." : "Recalculate"}
        </Button>
      </Group>

      {/* Confirmation Modal */}
      <Modal
        opened={isLockConfirmModalOpen}
        onClose={() => setIsLockConfirmModalOpen(false)}
        title="Confirm Locking"
      >
        <Blockquote color="blue">
          Are you sure you want to lock this ship? This action cannot be undone.
        </Blockquote>
        <Group mt="md">
          <Button onClick={() => setIsLockConfirmModalOpen(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={() => handleSubmit(true)}>
            Yes, lock it
          </Button>
        </Group>
      </Modal>

      {openedField ? (
        <TooltipComponent
          data={fieldInfoContent[openedField]}
          clickPosition={clickPosition}
          action={closeTooltip}
        />
      ) : null}

      {/* Display data table and graphs */}
      {displayData ? (
        <>
          <PlotlyGraphs data={displayData} />
        </>
      ) : null}
    </div>
  );
});

export default ShipFormSummary;
